/**
 * Cookiebot utility functions for managing cookie consent
 */

/**
 * Check if Cookiebot is properly configured
 * @returns {boolean} True if CBID is set and Cookiebot is available
 */
export const isCookiebotEnabled = () => {
  return !!(process.env.REACT_APP_CBID && window.Cookiebot);
};

/**
 * Get the Cookiebot object
 * @returns {object|null} The Cookiebot object
 */
export const getCookiebotInstance = () => {
  return window.Cookiebot || null;
};

/**
 * Show the Cookiebot consent dialog
 */
export const showCookieConsent = () => {
  // Use waitForCookiebot to ensure the script is fully loaded
  waitForCookiebot(() => {
    try {
      const cookiebot = getCookiebotInstance();
      if (!cookiebot) {
        console.error("Cookiebot not available");
        return;
      }

      // Use renew() method to show the consent dialog for changing preferences
      if (typeof cookiebot.renew === "function") {
        cookiebot.renew();
      } else {
        console.error("Cookiebot.renew() method is not available");
      }
    } catch (error) {
      console.error("Error calling Cookiebot.renew():", error);
    }
  });
};

/**
 * Hide the Cookiebot consent dialog
 */
export const hideCookieConsent = () => {
  const cookiebot = getCookiebotInstance();
  if (cookiebot && cookiebot.hide) {
    cookiebot.hide();
  }
};

/**
 * Get current consent state
 * @returns {object|null} Consent state object or null if not available
 */
export const getConsentState = () => {
  const cookiebot = getCookiebotInstance();
  if (cookiebot && cookiebot.consent) {
    return {
      necessary: cookiebot.consent.necessary,
      preferences: cookiebot.consent.preferences,
      statistics: cookiebot.consent.statistics,
      marketing: cookiebot.consent.marketing,
    };
  }
  return null;
};

/**
 * Check if a specific cookie category has consent
 * @param {string} category - Cookie category (necessary, preferences, statistics, marketing)
 * @returns {boolean} True if consent is given for the category
 */
export const hasConsentFor = (category) => {
  const consent = getConsentState();
  return consent ? consent[category] : false;
};

/**
 * Wait for Cookiebot to be ready
 * @param {function} callback - Function to call when Cookiebot is ready
 * @param {number} maxAttempts - Maximum number of attempts (default: 50)
 */
export const waitForCookiebot = (callback, maxAttempts = 50) => {
  let attempts = 0;

  const checkCookiebot = () => {
    attempts++;

    const cookiebot = getCookiebotInstance();
    if (cookiebot) {
      callback();
    } else if (attempts < maxAttempts) {
      setTimeout(checkCookiebot, 100);
    } else {
      console.warn("Cookiebot failed to load after maximum attempts");
    }
  };

  checkCookiebot();
};

/**
 * Add event listener for consent changes
 * @param {function} callback - Function to call when consent changes
 */
export const onConsentChanged = (callback) => {
  if (typeof callback !== "function") {
    console.warn("onConsentChanged requires a callback function");
    return;
  }

  waitForCookiebot(() => {
    // Listen for consent updates
    window.addEventListener("CookiebotOnConsentReady", callback);
    window.addEventListener("CookiebotOnDialogDisplay", callback);
    window.addEventListener("CookiebotOnAccept", callback);
    window.addEventListener("CookiebotOnDecline", callback);
  });
};

/**
 * Remove consent change event listeners
 * @param {function} callback - The callback function to remove
 */
export const removeConsentListener = (callback) => {
  window.removeEventListener("CookiebotOnConsentReady", callback);
  window.removeEventListener("CookiebotOnDialogDisplay", callback);
  window.removeEventListener("CookiebotOnAccept", callback);
  window.removeEventListener("CookiebotOnDecline", callback);
};

/**
 * Check if user has made a consent decision
 * @returns {boolean} True if user has made a consent decision
 */
export const hasUserConsented = () => {
  const cookiebot = getCookiebotInstance();
  if (cookiebot && typeof cookiebot.hasResponse !== "undefined") {
    return cookiebot.hasResponse;
  }
  return false;
};

/**
 * Get the consent ID (useful for tracking)
 * @returns {string|null} Consent ID or null if not available
 */
export const getConsentID = () => {
  const cookiebot = getCookiebotInstance();
  if (cookiebot && cookiebot.consentID) {
    return cookiebot.consentID;
  }
  return null;
};

/**
 * Renew consent (force user to consent again)
 */
export const renewConsent = () => {
  const cookiebot = getCookiebotInstance();
  if (cookiebot && cookiebot.renew) {
    cookiebot.renew();
  } else {
    console.warn("Cookiebot renew function not available");
  }
};
